import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  Paper, Table, TableBody, TableCell, TableHead, TableRow,
  Typography, CircularProgress, TablePagination, IconButton,
  Box, Chip, Stack, Tooltip
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { deleteBodyMap, getBodyMaps } from "../../../../../api/services/Assessment/BodyMap";
import ConfirmModal from "../../../../ConfirmModal/ConfirmModal";
import BodyMapDialog from "../../Assessments/ActiveCareTabs/CarePlan/Dialog/BodyMapDialog";
import { formatDate, formatTime } from "../../../../../utils/time";
import { QK } from "../../../../../base/qk";

const getSide = (location) => {
  const frontParts = ["Head", "Face", "Chest", "Abdomen", "Left Arm", "Right Arm", "Left Hand", "Right Hand", "Left Leg", "Right Leg", "Left Foot", "Right Foot"];
  if (frontParts.includes(location)) return "Front";
  return "Back";
};

const BodyMapRecordsTable = ({ clientID }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const queryClient = useQueryClient();
  const [editData, setEditData] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null });

  const { data, isLoading, isError } = useQuery(
    [QK.bodyMaps, clientID, page + 1, rowsPerPage],
    () => getBodyMaps(clientID, page + 1, rowsPerPage),
    { enabled: !!clientID }
  );

  const records = data?.results || [];
  const totalCount = data?.count || 0;

  const handleEdit = (record) => {
    setEditData(record);
    setDialogOpen(true);
  };

  const handleDelete = (id) => {
    setConfirmDelete({ open: true, id });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const deleteMutation = useMutation((id) => deleteBodyMap(id), {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.bodyMaps, clientID]);
      setConfirmDelete({ open: false, id: null });
    }
  });

  if (isLoading) return <Box sx={{ textAlign: 'center', p: 3 }}><CircularProgress size={24} /></Box>;
  if (isError) return <Typography color="error" sx={{ p: 2 }}>Error loading records.</Typography>;

  return (
    <>
      <Paper elevation={0} sx={{ border: "1px solid #eef2f6", borderRadius: "12px", overflow: "hidden", mt: 1 }}>
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 450 }}>
            <TableHead sx={{ bgcolor: "#f8fafc" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 800, fontSize: '11px', color: "#64748b", py: 1.5 }}>DETAILS</TableCell>
                <TableCell sx={{ fontWeight: 800, fontSize: '11px', color: "#64748b" }}>TYPE/SIDE</TableCell>
                <TableCell sx={{ fontWeight: 800, fontSize: '11px', color: "#64748b" }}>DATE</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800, fontSize: '11px', color: "#64748b", pr: 2 }}>ACTION</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((rec) => (
                <TableRow key={rec.id} hover>
                  <TableCell sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '13px', color: "#1e293b", lineHeight: 1.2 }}>
                      {rec.location}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#64748b",
                        display: 'block',
                        maxWidth: '140px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                      title={rec.issue}
                    >
                      {rec.issue}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Stack spacing={0.3}>
                      <Chip
                        label={rec.issue_type?.name || "Minor"}
                        size="small"
                        sx={{
                          height: '16px', fontSize: '9px', fontWeight: 900,
                          bgcolor: rec.issue_type?.name?.toLowerCase() === 'major' ? '#fff1f2' : '#f0f9ff',
                          color: rec.issue_type?.name?.toLowerCase() === 'major' ? '#e11d48' : '#0284c7',
                          width: 'fit-content', px: 0
                        }}
                      />
                      <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8' }}>
                        {getSide(rec.location)}
                      </Typography>
                    </Stack>
                  </TableCell>

                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#475569' }}>
                      {formatDate(rec.date_time)}
                    </Typography>
                    <Typography sx={{ fontSize: '10px', color: '#94a3b8' }}>
                      {formatTime(rec.date_time)}
                    </Typography>
                  </TableCell>

                  <TableCell align="right" sx={{ pr: 1 }}>
                    <Stack direction="row" spacing={0} justifyContent="flex-end">
                      <Tooltip title="Edit">
                        <IconButton onClick={() => handleEdit(rec)} size="small" sx={{ color: '#64748b' }}>
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton onClick={() => handleDelete(rec.id)} size="small" sx={{ color: '#f43f5e' }}>
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10]}
          sx={{
            borderTop: '1px solid #eef2f6',
            ".MuiTablePagination-toolbar": { minHeight: '35px', p: 0 },
            ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": { fontSize: '11px', m: 0 }
          }}
        />
      </Paper>

      <BodyMapDialog
        open={dialogOpen}
        handleClose={() => { setDialogOpen(false); setEditData(null); }}
        clientID={clientID}
        initialData={editData}
        onSubmit={() => queryClient.invalidateQueries([QK.bodyMaps, clientID])}
      />

      <ConfirmModal
        show={confirmDelete.open}
        title="Delete Record"
        body="Are you sure?"
        handleClose={() => setConfirmDelete({ open: false, id: null })}
        handleConfirm={() => deleteMutation.mutate(confirmDelete.id)}
        isLoading={deleteMutation.isLoading}
        isDelete
      />
    </>
  );
};

export default BodyMapRecordsTable;